import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useUrlState } from '../hooks/useUrlState';
import { apiClient } from '../lib/api-client';

type PlanPrice = {
  id: string;
  currencyCode: string;
  priceMonthly: number | null;
  priceYearly: number | null;
};

type Plan = {
  id: string;
  tier: string;
  name: string;
  displayName: Record<string, string>;
  description: Record<string, string>;
  prices: PlanPrice[];
  limits: Record<string, number | boolean | null>;
  features: Record<string, string[]>;
  displayOrder: number;
  isActive: boolean;
  isVisible: boolean;
};

const tierStyles: Record<string, string> = {
  FREE: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  BASIC: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  PREMIUM: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  ENTERPRISE: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
};

// URL に不正値が入った場合のフォールバック用の許可値。
const PLAN_FILTERS = ['all', 'active', 'inactive', 'visible', 'hidden'] as const;

function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // フィルタは URL クエリで管理し、戻る / 進む と URL 共有に対応する
  const [{ filter }, setUrlState] = useUrlState({ filter: 'all' }, { filter: PLAN_FILTERS });
  const [publishingPlans, setPublishingPlans] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishErrorDetails, setPublishErrorDetails] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      // Always fetch all plans (including inactive and invisible)
      const data = await apiClient.getPlans({
        includeInactive: true,
        includeInvisible: true
      });
      setPlans(data.plans);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (planId: string, isActive: boolean) => {
    try {
      await apiClient.togglePlanActive(planId, !isActive);
      await loadPlans();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle plan status');
    }
  };

  const handleToggleVisible = async (planId: string, isVisible: boolean) => {
    try {
      await apiClient.togglePlanVisibility(planId, !isVisible);
      await loadPlans();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle plan visibility');
    }
  };

  const handlePublishPlans = async () => {
    try {
      setPublishingPlans(true);
      setPublishError(null);
      setPublishErrorDetails(null);
      setPublishSuccess(null);

      const result = await apiClient.publishPlans();
      setPublishSuccess(
        `Successfully published ${result.publishedCount} plans to R2 (${result.individualUrls.length} individual files)`
      );
    } catch (err) {
      console.error('Failed to publish plans:', err);
      setPublishError(err instanceof Error ? err.message : 'Failed to publish plans');
      // Extract details from error object if available
      if (err && typeof err === 'object' && 'details' in err) {
        setPublishErrorDetails((err as any).details);
      }
    } finally {
      setPublishingPlans(false);
    }
  };

  const filteredPlans = plans.filter(plan => {
    if (filter === 'all') return true;
    if (filter === 'active') return plan.isActive;
    if (filter === 'inactive') return !plan.isActive;
    if (filter === 'visible') return plan.isVisible;
    if (filter === 'hidden') return !plan.isVisible;
    return true;
  });

  if (loading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-lg text-gray-600 dark:text-gray-300'>Loading plans...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
        <p className='text-red-800 dark:text-red-200'>Error: {error}</p>
        <button
          onClick={loadPlans}
          className='mt-2 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700'
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Plans</h1>
        <div className='flex flex-col gap-2 sm:flex-row'>
          <button
            onClick={handlePublishPlans}
            disabled={publishingPlans}
            className='rounded-md bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {publishingPlans ? 'Publishing...' : 'Publish'}
          </button>
          <Link
            to='/plans/create'
            className='rounded-md bg-blue-600 px-4 py-2 text-center text-white transition-colors hover:bg-blue-700'
          >
            Create New Plan
          </Link>
        </div>
      </div>

      {/* Success/Error Messages */}
      {publishSuccess && (
        <div className='flex items-start justify-between rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20'>
          <div className='flex items-start gap-3'>
            <svg
              className='mt-0.5 h-5 w-5 text-green-600 dark:text-green-400'
              fill='currentColor'
              viewBox='0 0 20 20'
            >
              <path
                fillRule='evenodd'
                d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                clipRule='evenodd'
              />
            </svg>
            <p className='text-green-800 dark:text-green-200'>{publishSuccess}</p>
          </div>
          <button
            onClick={() => setPublishSuccess(null)}
            className='text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300'
          >
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        </div>
      )}

      {publishError && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <div className='flex items-start justify-between'>
            <div className='flex items-start gap-3'>
              <svg
                className='mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                  clipRule='evenodd'
                />
              </svg>
              <div className='flex-1'>
                <p className='font-semibold text-red-800 dark:text-red-200'>{publishError}</p>
                {publishErrorDetails && (
                  <details className='mt-2'>
                    <summary className='cursor-pointer text-sm font-medium text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100'>
                      View error details
                    </summary>
                    <div className='mt-2 rounded-md bg-red-100 p-3 dark:bg-red-900/30'>
                      <pre className='text-xs break-words whitespace-pre-wrap text-red-900 dark:text-red-100'>
                        {publishErrorDetails}
                      </pre>
                    </div>
                  </details>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setPublishError(null);
                setPublishErrorDetails(null);
              }}
              className='text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
            >
              <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className='flex flex-wrap gap-2'>
        <button
          onClick={() => setUrlState({ filter: 'all' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'all'
              ? 'bg-gray-900 text-white dark:bg-gray-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          All ({plans.length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'active' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'active'
              ? 'bg-gray-900 text-white dark:bg-gray-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Active ({plans.filter(p => p.isActive).length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'inactive' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'inactive'
              ? 'bg-gray-900 text-white dark:bg-gray-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Inactive ({plans.filter(p => !p.isActive).length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'visible' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'visible'
              ? 'bg-gray-900 text-white dark:bg-gray-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Visible ({plans.filter(p => p.isVisible).length})
        </button>
        <button
          onClick={() => setUrlState({ filter: 'hidden' })}
          className={`rounded-md px-4 py-2 ${
            filter === 'hidden'
              ? 'bg-gray-900 text-white dark:bg-gray-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Hidden ({plans.filter(p => !p.isVisible).length})
        </button>
      </div>

      {/* Plans Grid */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
        {filteredPlans.length === 0 ? (
          <div className='col-span-full py-12 text-center text-gray-500 dark:text-gray-400'>
            No plans found
          </div>
        ) : (
          filteredPlans.map(plan => (
            <div
              key={plan.id}
              className='rounded-lg bg-white p-6 shadow-md transition-shadow hover:shadow-lg dark:bg-gray-800 dark:shadow-gray-900/50'
            >
              {/* Header */}
              <div className='mb-4 flex items-start justify-between'>
                <div>
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-semibold ${
                      tierStyles[plan.tier] ||
                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {plan.tier}
                  </span>
                  <h2 className='mt-2 text-xl font-bold text-gray-900 dark:text-white'>
                    {plan.displayName?.en || plan.name}
                  </h2>
                </div>
                <div className='flex flex-col gap-1'>
                  <span
                    className={`rounded px-2 py-1 text-xs font-semibold ${
                      plan.isActive
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}
                  >
                    {plan.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span
                    className={`rounded px-2 py-1 text-xs font-semibold ${
                      plan.isVisible
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {plan.isVisible ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className='mb-4 line-clamp-2 text-sm text-gray-600 dark:text-gray-400'>
                {plan.description?.en || 'No description'}
              </p>

              {/* Pricing */}
              {plan.prices && plan.prices.length > 0 && (
                <div className='mb-4 space-y-1'>
                  {plan.prices.map(price => (
                    <div key={price.id}>
                      {price.priceMonthly !== null && (
                        <div className='text-lg font-bold text-gray-900 dark:text-white'>
                          {price.currencyCode.toUpperCase()} {price.priceMonthly.toLocaleString()}
                          <span className='text-sm font-normal text-gray-600 dark:text-gray-400'>
                            /mo
                          </span>
                          {price.priceYearly !== null && (
                            <span className='ml-2 text-sm text-gray-600 dark:text-gray-400'>
                              ({price.priceYearly.toLocaleString()}/yr)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Key Limits */}
              <div className='mb-4 space-y-1'>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Pending Posts:</span>{' '}
                  {plan.limits?.pendingPostsLimit === -1
                    ? 'Unlimited'
                    : plan.limits?.pendingPostsLimit || 0}
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Pending Delete Actions:</span>{' '}
                  {plan.limits?.pendingDeleteActionsLimit === -1
                    ? 'Unlimited'
                    : (plan.limits?.pendingDeleteActionsLimit ?? 0)}
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Actions Per Slot:</span>{' '}
                  {plan.limits?.actionsPerSlotLimit === -1
                    ? 'Unlimited'
                    : (plan.limits?.actionsPerSlotLimit ?? 0)}
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Interval:</span>{' '}
                  {plan.limits?.scheduleIntervalMinutes || 15} min
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Max Length:</span>{' '}
                  {plan.limits?.maxPostLength || 300} chars
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Thread Posts:</span>{' '}
                  {plan.limits?.threadPosts ? (
                    <span className='text-green-600 dark:text-green-400'>
                      ✓ (Max: {plan.limits?.threadPostsLimit || 1} child posts)
                    </span>
                  ) : (
                    <span className='text-red-600 dark:text-red-400'>✗</span>
                  )}
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Storage:</span>{' '}
                  {plan.limits?.storage_mb === -1
                    ? 'Unlimited'
                    : `${plan.limits?.storage_mb || 0} MB`}
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  <span className='font-semibold'>Order:</span> {plan.displayOrder}
                </div>
              </div>

              {/* Actions */}
              <div className='flex gap-2'>
                <Link
                  to={`/plans/${plan.id}/edit`}
                  className='flex-1 rounded-md bg-blue-600 px-3 py-2 text-center text-sm text-white transition-colors hover:bg-blue-700'
                >
                  Edit
                </Link>
                <button
                  onClick={() => handleToggleActive(plan.id, plan.isActive)}
                  className={`flex-1 rounded-md px-3 py-2 text-sm transition-colors ${
                    plan.isActive
                      ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50'
                      : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50'
                  }`}
                >
                  {plan.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleToggleVisible(plan.id, plan.isVisible)}
                  className='rounded-md bg-gray-200 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  title={plan.isVisible ? 'Hide plan' : 'Show plan'}
                >
                  <FontAwesomeIcon icon={plan.isVisible ? faEye : faEyeSlash} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default PlansPage;
